// AdminGrowthSpreadReviewPage.tsx — Phase 4: the shared candidate-review surface for the still
// (image) and carousel spread pipelines. One component driven by `kind` (4.1):
//   - generate N candidates (spreadCount) → the run parks at the spread gate
//   - poll run status (4.2): while pre-spread, show a placeholder-with-clock; when awaiting_review,
//     load the spread and render each candidate's preview; on failed, show the error + re-spread.
//   - pick one candidate (4.4) — the pick IS the approval (freezes + resumes → finalize renders it).
//   - regenerate = re-spread with an edited spreadCount (4.3) — destructive-confirm first (4.7),
//     because a new spread replaces the current candidates (no content history).
//   - inputs on the card (4.5): the arc's generation_inputs (niche/pattern/character/audience),
//     read-only, shown so "what produced this" is visible next to the output.
//
// Carousel's per-slide review (after a pick) is the existing storyboard drill-down
// (AdminGrowthArcsPage) — a slide is a beat; this page hands off to it via a link.
//
// Reachable at /admin/growth/arcs/:arcId/review/:kind  (kind = still | carousel).
// Styling matches AdminGrowthArcsPage's inline-token system (no Tailwind here, same as that page).
import { useProgress } from "@/components/ui/ProgressContext.tsx";
import { useState, useEffect, useCallback, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import {
  growthApi, growthSpreadApi, growthQueueApi,
  subscribeToRun, sessionCost,
  type GrowthArcReview, type SpreadView, type SpreadCandidate, type RunStatus,
} from "../../api/growth.ts";

const wrap: React.CSSProperties = { padding: 16, maxWidth: 1100, margin: "0 auto" };
const card: React.CSSProperties = { border: "1px solid #e5e7eb", borderRadius: 10, padding: 14, background: "#fff", marginBottom: 10 };
const btn: React.CSSProperties = { padding: "6px 12px", borderRadius: 7, border: "1px solid #d1d5db", background: "#fff", cursor: "pointer", fontSize: 13 };
const btnPrimary: React.CSSProperties = { ...btn, background: "#2f855a", color: "#fff", border: 0 };
const btnDanger: React.CSSProperties = { ...btn, background: "#fff", color: "#b91c1c", borderColor: "#fca5a5" };
const muted: React.CSSProperties = { fontSize: 12, color: "#6b7280" };
const grid: React.CSSProperties = { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: 12 };
const label: React.CSSProperties = { fontSize: 11, textTransform: "uppercase", letterSpacing: 0.4, color: "#9ca3af" };

const POLL_MS = 3000;

function phaseLabel(s: RunStatus | null): string {
  if (!s) return "loading…";
  const ph = s.progress?.phase ?? s.status ?? "…";
  const pct = s.progress?.pct;
  return pct != null ? `${ph} (${pct}%)` : String(ph);
}

/** A single candidate card: preview (or clock while rendering), axis values, pick action. */
function CandidateCard(props: {
  kind: "still" | "carousel";
  c: SpreadCandidate;
  busy: boolean;
  onPick: (candidateId: string) => void;
}) {
  const { kind, c, busy, onPick } = props;
  const previews = kind === "still" ? (c.previewUrl ? [c.previewUrl] : []) : (c.previewUrls ?? []);
  return (
    <div style={card}>
      {previews.length > 0 ? (
        kind === "carousel" ? (
          <div style={{ display: "flex", gap: 4, overflowX: "auto", marginBottom: 8 }}>
            {previews.map((u, i) => (
              <img key={i} src={u} alt={`slide ${i + 1}`} style={{ height: 140, borderRadius: 6, flex: "0 0 auto" }} />
            ))}
          </div>
        ) : (
          <img src={previews[0]} alt="candidate" style={{ width: "100%", borderRadius: 6, marginBottom: 8 }} />
        )
      ) : (
        <div style={{ height: 140, borderRadius: 6, background: "#f3f4f6", display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 8, ...muted }}>
          🕐 rendering preview…
        </div>
      )}
      {kind === "carousel" && c.slideCount != null && <div style={muted}>{c.slideCount} slides</div>}
      {c.axisValues && Object.keys(c.axisValues).length > 0 && (
        <div style={{ margin: "6px 0" }}>
          <div style={label}>axis values</div>
          <div style={{ ...muted, wordBreak: "break-word" }}>
            {Object.entries(c.axisValues).map(([k, v]) => `${k}: ${String(v)}`).join(" · ")}
          </div>
        </div>
      )}
      <button style={btnPrimary} disabled={busy} onClick={() => onPick(c.candidateId)}>
        {busy ? "picking…" : "Pick this"}
      </button>
    </div>
  );
}

export function AdminGrowthSpreadReviewPage() {
  const { track } = useProgress();
  const { arcId = "", kind: kindParam } = useParams<{ arcId: string; kind: string }>();
  const kind: "still" | "carousel" = kindParam === "carousel" ? "carousel" : "still";

  const [arc, setArc] = useState<GrowthArcReview | null>(null);
  const [arcErr, setArcErr] = useState<string | null>(null);
  const [runId, setRunId] = useState<string | null>(null);
  const [status, setStatus] = useState<RunStatus | null>(null);
  const [spread, setSpread] = useState<SpreadView | null>(null);
  const [spreadCount, setSpreadCount] = useState(kind === "carousel" ? 3 : 4);
  const [busy, setBusy] = useState(false);
  const [picking, setPicking] = useState<string | null>(null);
  const [note, setNote] = useState<string>("");
  const [confirmRegen, setConfirmRegen] = useState(false);
  const [sessionRenders, setSessionRenders] = useState(sessionCost.get());
  const unsubRef = useRef<(() => void) | null>(null);

  const api = kind === "carousel"
    ? { gen: growthSpreadApi.generateCarousel, read: growthSpreadApi.readCarousel, pick: growthSpreadApi.pickCarousel }
    : { gen: growthSpreadApi.generateStill, read: growthSpreadApi.readStill, pick: growthSpreadApi.pickStill };

  const cap = kind === "carousel" ? 8 : 12;

  // load the arc header (for the inputs-on-card context + niche/pattern display).
  useEffect(() => {
    // (tf3-31, A17) a failed read is shown, never a page without its arc
    setArcErr(null);
    growthApi.arcDetail(arcId).then(setArc).catch((e) => { setArc(null); setArcErr(String((e as Error)?.message ?? e)); });   // catch-reason: the error is shown in the header
  }, [arcId]);

  const stopWatch = useCallback(() => {
    if (unsubRef.current) { unsubRef.current(); unsubRef.current = null; }
  }, []);

  const loadSpread = useCallback(async () => {
    try { setSpread(await api.read(arcId)); } catch { /* not at the gate yet — keep watching */ }
  }, [api, arcId]);

  // Watch the run via subscribeToRun (SSE push + polling fallback). On each change, re-read
  // authoritative status; when it reaches awaiting_review load the spread and stop watching.
  const watch = useCallback((rid: string) => {
    stopWatch();
    const onChange = async () => {
      try {
        const s = await growthQueueApi.runStatus(arcId, rid);
        setStatus(s);
        const ph = s.progress?.phase;
        if (ph === "awaiting_review") { await loadSpread(); stopWatch(); }
        if (s.status === "failed") stopWatch();
      } catch { /* transient — keep watching */ }
    };
    unsubRef.current = subscribeToRun(arcId, rid, onChange, { pollMs: POLL_MS });
    onChange();   // immediate first read
  }, [arcId, loadSpread, stopWatch]);

  useEffect(() => stopWatch, [stopWatch]);

  const generate = useCallback(async () => {
    setBusy(true); setSpread(null); setStatus(null); setConfirmRegen(false);
    try {
      const r = await track("Making the candidates", api.gen(arcId, spreadCount),
        { detail: `${spreadCount} candidate(s) — they appear here as they finish` });
      setRunId(r.runId);
      setSessionRenders(sessionCost.add(spreadCount));   // Phase 5.0: session render tally
      setNote(`Placed spread order (${spreadCount} candidates). Rendering…`);
      watch(r.runId);
    } catch (e) {
      setNote(e instanceof Error ? e.message : "generate failed");
    } finally { setBusy(false); }
  }, [api, arcId, spreadCount, watch]);

  const pick = useCallback(async (candidateId: string) => {
    setPicking(candidateId);
    try {
      await track("Picking this one", api.pick(arcId, candidateId), { detail: "it goes on to the final version" });
      setNote(`Picked ${candidateId}. The run is finalizing the chosen ${kind}.`);
      setSpread(null);
      if (runId) watch(runId);   // resume watching to show it advance past the gate
    } catch (e) {
      setNote(e instanceof Error ? e.message : "pick failed");
    } finally { setPicking(null); }
  }, [api, arcId, kind, runId, watch]);

  const failed = status?.status === "failed";

  return (
    <div style={wrap}>
      <div style={{ marginBottom: 12 }}>
        <Link to="/admin/growth/queue" style={muted}>← Queue</Link>
        {" · "}
        <Link to={`/admin/growth/arcs/${arcId}`} style={muted}>Arc detail</Link>
      </div>
      <h2 style={{ margin: "0 0 4px" }}>{kind === "carousel" ? "Carousel" : "Image"} review</h2>
      {arcErr && <div style={{ color: "#b91c1c", fontSize: 13 }}>The arc couldn't be read: {arcErr}</div>}
      {arc && (
        <div style={{ ...muted, marginBottom: 12 }}>
          {/* inputs-on-card context (4.5): what produced these candidates */}
          niche: <b>{arc.niche}</b> · pattern: <b>{arc.patternId}</b>
          {arc.premise ? <> · {arc.premise}</> : null}
        </div>
      )}

      {/* generate / regenerate controls */}
      <div style={{ ...card, display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
        <label style={{ ...muted, display: "flex", alignItems: "center", gap: 6 }}>
          candidates
          <input
            type="number" min={1} max={cap} value={spreadCount}
            onChange={(e) => setSpreadCount(Math.max(1, Math.min(cap, Number(e.target.value) || 1)))}
            style={{ width: 56, padding: "4px 6px", borderRadius: 6, border: "1px solid #d1d5db" }}
          />
          <span style={{ color: "#9ca3af" }}>(max {cap})</span>
        </label>
        {!spread && !status ? (
          <button style={btnPrimary} disabled={busy} onClick={generate}>
            {busy ? "placing…" : `Generate ${spreadCount} candidates`}
          </button>
        ) : confirmRegen ? (
          <>
            <span style={{ fontSize: 13, color: "#b91c1c" }}>
              Regenerate replaces the current candidates — they’re lost (no history). Continue?
            </span>
            <button style={btnDanger} onClick={generate}>Yes, re-spread</button>
            <button style={btn} onClick={() => setConfirmRegen(false)}>Cancel</button>
          </>
        ) : (
          <button style={btn} onClick={() => setConfirmRegen(true)}>Regenerate (re-spread)</button>
        )}
        {/* estimated render count — cost signal (cheap pipelines invite blind spend) */}
        <span style={muted}>≈ {spreadCount} render{spreadCount === 1 ? "" : "s"}</span>
        <span style={{ flex: 1 }} />
        <span style={muted} title="renders triggered this session (per tab; resets on reload)">
          session: {sessionRenders} render{sessionRenders === 1 ? "" : "s"}
        </span>
      </div>

      {note && <div style={{ ...card, background: "#f8fafc" }}>{note}</div>}

      {/* status line while rendering / parked */}
      {status && !spread && !failed && (
        <div style={{ ...card, display: "flex", alignItems: "center", gap: 10 }}>
          <span>🕐</span>
          <span>Run status: <b>{phaseLabel(status)}</b></span>
          <span style={muted}>live (push + {POLL_MS / 1000}s poll)…</span>
        </div>
      )}

      {/* failure + recovery */}
      {failed && (
        <div style={{ ...card, borderColor: "#fca5a5", background: "#fef2f2" }}>
          <div style={{ color: "#b91c1c", fontWeight: 600, marginBottom: 4 }}>
            Render failed{status?.failure?.category ? ` — ${status.failure.category}` : ""}
          </div>
          {status?.failure?.reason && <div style={{ ...muted, marginBottom: 8 }}>{status.failure.reason}</div>}
          <button style={btnPrimary} onClick={generate}>Re-spread (try again)</button>
        </div>
      )}

      {/* the candidate grid */}
      {spread && spread.candidates.length > 0 && (
        <>
          <div style={{ ...muted, margin: "8px 0" }}>
            {spread.candidates.length} candidate{spread.candidates.length === 1 ? "" : "s"} — pick the one you want.
            {spread.candidates.length < spreadCount && (
              <span style={{ color: "#d97706" }}> (asked for {spreadCount}; the spread returned fewer)</span>
            )}
          </div>
          <div style={grid}>
            {spread.candidates.map((c) => (
              <CandidateCard key={c.candidateId} kind={kind} c={c} busy={picking === c.candidateId} onPick={pick} />
            ))}
          </div>
        </>
      )}

      {/* carousel: after a pick, per-slide review is the storyboard drill-down */}
      {kind === "carousel" && arc && (
        <div style={{ ...card, marginTop: 12 }}>
          <div style={label}>after picking</div>
          <div style={muted}>
            Refine individual slides in the{" "}
            <Link to={`/admin/growth/arcs/${arcId}`}>storyboard view</Link> (a slide is a beat —
            regenerate or edit each, then approve).
          </div>
        </div>
      )}
    </div>
  );
}
